import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { LoaderData } from "~/types";
import { INTENTS, loadCookiesData } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { message } from "antd";
import { t } from "i18next";
import { apiClient, AddParcelPageLoaderResult } from "~/services/apiClient";

export const AddParcelPageLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<AddParcelPageLoaderType> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) redirect("/explore");
  const userUID = user!.uid;
  const authAccessInfo = await loadCookiesData(userUID);
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const { searchParams } = new URL(request.url);
  const initialPropertyID = searchParams.get("propertyID") ?? "";

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await apiClient.loader<AddParcelPageLoaderResult>(
    "add-parcel-page",
    {
      ownerID,
      ownerType,
      userID: userUID,
      initialPropertyID: initialPropertyID || undefined,
      isAdminOrSuperAdmin: String(isAdminOrSuperAdmin),
    },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load add parcel page:", result.error);
    throw new Error(result.error ?? "Failed to load add parcel page");
  }

  const data = result.data;
  const propertyList: LoaderData<APITypes.Property[]> = {
    success: true,
    data: data.propertyList,
  };

  return {
    propertyList,
    initialProperty: data.initialProperty,
    canCreateParcel: data.canCreateParcel,
    isAuthenticated: true,
  };
};

export type AddParcelPageLoaderType = {
  propertyList: LoaderData<APITypes.Property[]>;
  initialProperty: APITypes.Property;
  canCreateParcel: boolean;
  isAuthenticated: boolean;
};

interface AddParcelActionResult {
  created: number;
  id?: string;
  propertyID?: string;
  redirectMultiple?: boolean;
  redirectSingle?: boolean;
}

export const AddParcelPageAction = async ({
  request,
}: LoaderFunctionArgs) => {
  const formData = await request.formData();
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { role } = authAccessInfo.data;

  const payload = JSON.parse(formData.get("body") as string) as APITypes.CreateParcelInput & {
    isAdminOrSuperAdmin?: boolean;
  };
  const propertyID = payload.propertyID;
  payload.status = APITypes.ParcelStatusType.None;
  payload.isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  return withEntityCacheInvalidation(
    [
      { entity: "parcel" as const },
      { entity: "property" as const },
    ],
    async () => {
    const result = await apiClient.sendIntent<AddParcelActionResult>(
      INTENTS._add_parcel,
      payload,
    );

    if (!result.success) {
      message.error(t("Error_creating_parcel"));
      console.error("Add parcel failed:", result.error);
      return redirect(propertyID ? `/property/${propertyID}` : "/explore");
    }

    const data = result.data;
    if (data?.redirectMultiple && data.propertyID) {
      message.success(t("Parcels_created_successfully"));
      return redirect(`/property/${data.propertyID}?tab=parcelList`);
    }
    if (data?.redirectSingle && data.id) {
      message.success(t("Parcel_created_successfully"));
      return redirect(`/parcel/${data.id}`);
    }

    message.error(t("Error_creating_parcel"));
    return redirect(propertyID ? `/property/${propertyID}` : "/explore");
  });
};
